"use client";

// Liquid Glass Carousel (21st.dev), adapté : la rangée d'images n'est plus infinie ni pilotée
// par la molette ou le glisser. Elle est épinglée au milieu de l'écran et c'est le scroll de la
// page qui la fait défiler, de la première à la dernière image ; ensuite la page reprend.
// On garde le rendu three.js, la lentille « verre liquide », l'entrée animée et le mode focus.

import { gsap } from "gsap";
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import * as THREE from "three";

export interface ScrollGlassCarouselItem {
  src: string;
  title: string;
  /** Width / height. Measured from the image when omitted. */
  aspect?: number;
}

export interface ScrollGlassCarouselProps {
  items: ScrollGlassCarouselItem[];
  /** Target panel height in CSS pixels. Scaled down when the screen is shorter. */
  panelHeight?: number;
  gap?: number;
  background?: string;
  /** Colour of the glowing ring of the lens. */
  ringColor?: string;
  /** Page scroll, in viewport heights, spent on each image. */
  scrollPerItem?: number;
  labels?: { region: string; view: string; close: string; of: string };
  /** Shown instead of the carousel when WebGL is unavailable. */
  fallback?: ReactNode;
  className?: string;
}

const LENS = {
  sizeX: 0.565,
  sizeY: 1,
  posX: 0.5,
  posY: 0.5,
  rotation: 65,
  zoom: 0,
  dispersion: 11,
  blur: 0,
  glow: 4.2,
  whiteGlow: 0.24,
  novaSize: 12,
  blueRing: 6,
  ringRadius: 0.49,
  ringWidth: 0.014,
  shimmer: true,
  shimmerFreq: 12,
  shimmerSpeed: 3.5,
  shimmerDepth: 0.12,
  rimStart: 0.578,
  rimTangential: 0.6,
  rimInward: 0,
  rimFreq1: 2,
  rimFreq2: 1,
  rimLine: 1.4,
  rimLinePos: 0.488,
  rimLineWidth: 0.003,
  vignette: 0,
  vignetteSize: 0.3,
  samples: 16,
};

const FOCUS = {
  cardDuration: 0.7,
  focusDuration: 0.9,
  cardEase: "power4.out",
  focusEase: "power3.out",
  stagger: 0.06,
  dropDist: 1.4,
  centerScale: 1.18,
  lensFade: 0.85,
};

const ENTRY = {
  startH: 80,
  riseDuration: 1.0,
  stagger: 0.07,
  riseEase: "power3.out",
  fromBelow: 0.9,
  growDelay: 0.25,
  growDuration: 2.15,
  growEase: "expo.inOut",
  growStagger: 0.085,
  lensBloom: 1.4,
  lensBloomEase: "power2.inOut",
};

const LENS_VERTEX = /* glsl */ `
varying vec2 vUv;
void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

const LENS_FRAGMENT = /* glsl */ `
#define PI 3.14159265
precision highp float;
varying vec2 vUv;
uniform sampler2D uTex;
uniform vec2 uRes;
uniform vec2 uCenter;
uniform float uSizeX;
uniform float uSizeY;
uniform float uAspect;
uniform float uZoom;
uniform float uDispersion;
uniform float uBlur;
uniform float uGlow;
uniform float uWhiteGlow;
uniform float uNovaSize;
uniform float uBlueRing;
uniform float uRingRadius;
uniform float uRingWidth;
uniform float uShimmer;
uniform float uShimmerFreq;
uniform float uShimmerSpeed;
uniform float uShimmerDepth;
uniform float uTime;
uniform float uRimStart;
uniform float uRimTangential;
uniform float uRimInward;
uniform float uRimFreq1;
uniform float uRimFreq2;
uniform vec3 uBlueColor;
uniform float uRimLine;
uniform float uRimLinePos;
uniform float uRimLineWidth;
uniform float uVignette;
uniform float uVignetteSize;
uniform float uRotation;
uniform int uSamples;

const int MAX_SAMPLES = 16;

vec3 discLens(vec2 center, float aspectCorrect, out float outA) {
  vec2 p = (vUv - center);
  p.x *= aspectCorrect;
  float ca = cos(uRotation), sa = sin(uRotation);
  p = mat2(ca, -sa, sa, ca) * p;
  vec2 halfSize = vec2(uSizeX, uSizeY);
  float dist = length(p / halfSize);
  outA = 0.0;
  if (dist > 1.0) return vec3(0.0);

  float nd = clamp(dist, 0.0, 1.0);
  vec2 offset = vUv - center;
  vec2 radialDir = normalize(offset + 1e-6);
  vec2 tangentDir = vec2(-radialDir.y, radialDir.x);
  float angle = atan(p.y, p.x);

  float pull = uZoom * 0.30 * (nd * nd);
  float rimStrength = smoothstep(uRimStart, 1.0, nd);
  float fluidWave = sin(angle * uRimFreq1) * 0.55 + sin(angle * uRimFreq2) * 0.25;
  float rScreen = (uSizeX + uSizeY) * 0.5;
  vec2 rimOff = tangentDir * fluidWave * rimStrength * rScreen * uRimTangential;
  vec2 rimPull = -radialDir * rimStrength * rScreen * uRimInward;

  vec2 baseUV = center + offset * (1.0 - pull) + rimOff + rimPull;

  float rimMask = smoothstep(0.55, 1.0, nd);
  vec2 dispDir = offset * uDispersion * 0.004 * rimMask;
  int N = uSamples;
  if (N < 2) N = 2;
  if (N > MAX_SAMPLES) N = MAX_SAMPLES;
  vec3 col = vec3(0.0);
  vec3 caW = vec3(0.0);
  for (int i = 0; i < MAX_SAMPLES; i++) {
    if (i >= N) break;
    float t = float(i) / float(N - 1);
    vec2 sUV = baseUV + dispDir * (t - 0.5);
    vec3 s = texture2D(uTex, sUV).rgb;
    vec3 w = vec3(
      exp(-pow((t - 0.00) / 0.38, 2.0)),
      exp(-pow((t - 0.50) / 0.38, 2.0)),
      exp(-pow((t - 1.00) / 0.38, 2.0))
    );
    col += s * w;
    caW += w;
  }
  col /= max(caW, vec3(0.001));

  float blurFade = 1.0 - smoothstep(0.72, 0.98, nd);
  if (uBlur > 0.01 && blurFade > 0.01) {
    vec2 blurRad = vec2(uBlur) / uRes * blurFade;
    vec3 bcol = vec3(0.0);
    float btw = 0.0;
    for (float a = 0.0; a < PI * 2.0; a += PI * 2.0 / 6.0) {
      for (float rr = 0.4; rr <= 1.001; rr += 0.3) {
        vec2 o = vec2(cos(a), sin(a)) * blurRad * rr;
        float w = 1.0 - rr * 0.38;
        bcol += texture2D(uTex, baseUV + o).rgb * w;
        btw += w;
      }
    }
    col = mix(bcol / btw, col, rimMask);
  }

  col *= mix(0.91, 1.0, smoothstep(0.0, 0.38, nd));

  float r2 = nd * nd * 0.25;
  float gs = max(uNovaSize * uGlow * 0.003, 0.004);
  float nova = exp(-r2 / gs) + exp(-r2 / (gs * 7.0)) * 0.18;
  nova *= uWhiteGlow * (uGlow / 17.0) * 1.15;
  col += vec3(nova);

  float dC = nd * 0.5;
  float tR = clamp(uRingRadius, 0.1, 0.49);
  float rW = max(uRingWidth, 0.003);
  float ring = exp(-pow((dC - tR) / rW, 2.0));
  ring *= uBlueRing * (uGlow / 17.0) * 1.8;
  if (uShimmer > 0.5) ring *= sin(angle * uShimmerFreq + uTime * uShimmerSpeed) * uShimmerDepth + (1.0 - uShimmerDepth);
  float ringAura = exp(-pow((dC - tR) / (rW * 6.0), 2.0)) * 0.28 * uBlueRing * (uGlow / 17.0);
  col += uBlueColor * (ring + ringAura);
  col += vec3(exp(-pow((dC - uRimLinePos) / max(uRimLineWidth, 0.0001), 2.0)) * uRimLine);

  outA = smoothstep(1.0, 0.93, dist);
  return col;
}

void main(){
  vec3 base = texture2D(uTex, vUv).rgb;
  float a = 0.0;
  vec3 c = discLens(uCenter, uAspect, a);
  vec3 outc = mix(base, c, a);
  if (uVignette > 0.001) {
    vec2 vc = vUv - 0.5;
    vc.x *= uAspect;
    float d = length(vc) / max(uVignetteSize, 0.0001);
    outc *= clamp(1.0 - uVignette * smoothstep(0.5, 1.0, d), 0.0, 1.0);
  }
  gl_FragColor = vec4(outc, 1.0);
}
`;

const LENS_FX_KEYS = ["uDispersion", "uBlueRing", "uRimLine", "uVignette", "uZoom", "uRimTangential", "uRimInward"] as const;

type Panel = { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial; bound: boolean };
type Source = { tex: THREE.Texture | null; aspect: number; locked: boolean };
type HitRect = { left: number; right: number; top: number; bottom: number; index: number };

type Engine = {
  /** 0 → first image centred, 1 → last image centred. */
  setProgress: (p: number) => void;
  playEntry: () => void;
  closeFocus: () => void;
  destroy: () => void;
};

function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function createCarousel(
  mount: HTMLElement,
  cursorElement: HTMLElement | null,
  options: {
    items: ScrollGlassCarouselItem[];
    panelHeight: number;
    gap: number;
    background: string;
    ringColor: string;
    onActiveChange: (index: number) => void;
    onFocusChange: (open: boolean) => void;
    onEntryDone: (done: boolean) => void;
    /** Clicked on a side image: the page should scroll to it. */
    onRequestIndex: (index: number) => void;
  },
): Engine | null {
  const reduced = prefersReducedMotion();
  const items = options.items;
  const N = items.length;
  if (N === 0) return null;

  let W = Math.max(1, mount.clientWidth);
  let H = Math.max(1, mount.clientHeight);
  const panelHFor = () => Math.max(120, Math.min(options.panelHeight, Math.round(H * 0.62)));
  let PANEL_H = panelHFor();
  const GAP = options.gap;
  const EASE = reduced ? 0.35 : 0.1;
  const SHRINK_MAX = 60;
  const SHRINK_ATTACK = 0.25;
  const SHRINK_DECAY = 0.06;

  let renderer: THREE.WebGLRenderer;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
  } catch {
    return null;
  }

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  renderer.setPixelRatio(dpr);
  renderer.setSize(W, H);
  renderer.setClearColor(new THREE.Color(options.background), 1);
  const el = renderer.domElement;
  Object.assign(el.style, { display: "block", width: "100%", height: "100%", userSelect: "none" });
  el.setAttribute("aria-hidden", "true");
  mount.appendChild(el);

  const scene = new THREE.Scene();
  const camera = new THREE.OrthographicCamera(-W / 2, W / 2, H / 2, -H / 2, -100, 100);
  camera.position.z = 10;

  // ---- images, laid out on one straight track
  const sources: Source[] = items.map((img) => ({ tex: null, aspect: img.aspect || 3 / 4, locked: img.aspect != null }));
  // a wide image on a narrow screen is lowered so it never exceeds 85 % of the width
  const heightOf = (i: number) => Math.min(PANEL_H, (W * 0.85) / sources[i].aspect);
  const widthOf = (i: number) => sources[i].aspect * heightOf(i);
  let offsets: number[] = [];
  const recompute = () => {
    offsets = [];
    let acc = 0;
    sources.forEach((_, i) => {
      offsets.push(acc);
      acc += widthOf(i) + GAP;
    });
  };
  const centerOf = (i: number) => offsets[i] + widthOf(i) / 2;
  const nearestIndex = (value: number) => {
    let best = 0;
    for (let i = 1; i < N; i++) if (Math.abs(centerOf(i) - value) < Math.abs(centerOf(best) - value)) best = i;
    return best;
  };
  recompute();

  let progress = 0;
  const targetFor = (p: number) => (N === 1 ? centerOf(0) : centerOf(0) + p * (centerOf(N - 1) - centerOf(0)));
  let scroll = targetFor(0);
  let target = scroll;

  // Images are decoded off the main thread (ImageBitmap) and sent to the GPU as soon as they
  // arrive, so the first frames of the entry don't stall on decoding or uploads.
  const loader = new THREE.ImageBitmapLoader();
  loader.setOptions({ imageOrientation: "flipY" });
  const ready = Promise.all(
    sources.map(
      (s, i) =>
        new Promise<void>((resolve) => {
          loader.load(
            items[i].src,
            (bitmap) => {
              const tex = new THREE.Texture(bitmap);
              tex.flipY = false; // already flipped by the bitmap
              tex.minFilter = THREE.LinearMipmapLinearFilter;
              tex.magFilter = THREE.LinearFilter;
              tex.generateMipmaps = true;
              tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
              tex.colorSpace = THREE.SRGBColorSpace;
              tex.needsUpdate = true;
              if (!s.locked) s.aspect = bitmap.width / bitmap.height;
              s.tex = tex;
              renderer.initTexture(tex);
              recompute();
              target = targetFor(progress);
              resolve();
            },
            undefined,
            () => resolve(),
          );
        }),
    ),
  );

  const panels: Panel[] = sources.map(() => {
    const mat = new THREE.MeshBasicMaterial({ color: 0xdddddd, transparent: true });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    mesh.visible = false;
    scene.add(mesh);
    return { mesh, mat, bound: false };
  });

  // ---- lens pass
  const rt = new THREE.WebGLRenderTarget(W * dpr, H * dpr);
  const lensScene = new THREE.Scene();
  const lensCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const lensUniforms = {
    uTex: { value: rt.texture },
    uRes: { value: new THREE.Vector2(W * dpr, H * dpr) },
    uCenter: { value: new THREE.Vector2(LENS.posX, LENS.posY) },
    uSizeX: { value: LENS.sizeX },
    uSizeY: { value: LENS.sizeY },
    uRotation: { value: (LENS.rotation * Math.PI) / 180 },
    uAspect: { value: W / H },
    uZoom: { value: LENS.zoom },
    uDispersion: { value: LENS.dispersion },
    uBlur: { value: LENS.blur },
    uGlow: { value: LENS.glow },
    uWhiteGlow: { value: LENS.whiteGlow },
    uNovaSize: { value: LENS.novaSize },
    uBlueRing: { value: LENS.blueRing },
    uRingRadius: { value: LENS.ringRadius },
    uRingWidth: { value: LENS.ringWidth },
    uShimmer: { value: reduced || !LENS.shimmer ? 0 : 1 },
    uShimmerFreq: { value: LENS.shimmerFreq },
    uShimmerSpeed: { value: LENS.shimmerSpeed },
    uShimmerDepth: { value: LENS.shimmerDepth },
    uTime: { value: 0 },
    uRimStart: { value: LENS.rimStart },
    uRimTangential: { value: LENS.rimTangential },
    uRimInward: { value: LENS.rimInward },
    uRimFreq1: { value: LENS.rimFreq1 },
    uRimFreq2: { value: LENS.rimFreq2 },
    uBlueColor: { value: new THREE.Color(options.ringColor) },
    uRimLine: { value: LENS.rimLine },
    uRimLinePos: { value: LENS.rimLinePos },
    uRimLineWidth: { value: LENS.rimLineWidth },
    uVignette: { value: LENS.vignette },
    uVignetteSize: { value: LENS.vignetteSize },
    uSamples: { value: LENS.samples },
  };
  const lensMat = new THREE.ShaderMaterial({
    uniforms: lensUniforms as unknown as THREE.ShaderMaterial["uniforms"],
    vertexShader: LENS_VERTEX,
    fragmentShader: LENS_FRAGMENT,
  });
  const lensQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), lensMat);
  lensScene.add(lensQuad);
  renderer.compile(lensScene, lensCam);
  ready.then(() => {
    if (!running) return;
    panels.forEach((p, i) => {
      const tex = sources[i].tex;
      if (tex && !p.bound) {
        p.mat.map = tex;
        p.mat.color.set(0xffffff);
        p.mat.needsUpdate = true;
        p.bound = true;
      }
    });
    // compile() only walks visible objects
    const was = panels.map((p) => p.mesh.visible);
    panels.forEach((p) => (p.mesh.visible = true));
    renderer.compile(scene, camera);
    panels.forEach((p, i) => (p.mesh.visible = was[i]));
  });
  const lensFxFull = Object.fromEntries(LENS_FX_KEYS.map((k) => [k, lensUniforms[k].value])) as Record<
    (typeof LENS_FX_KEYS)[number],
    number
  >;

  // ---- focus and entry state (animated by gsap)
  const focus = { active: false, index: -1, lensFx: reduced ? 1 : 0, anim: null as gsap.core.Timeline | null, atProgress: 0 };
  const drop = new Array(N).fill(0);
  let focusScale = 1;
  const pEntry = new Array(N).fill(reduced ? 1 : 0);
  const grow = new Array(N).fill(reduced ? 1 : 0);
  let entryState: "waiting" | "rising" | "growing" | "done" = reduced ? "done" : "waiting";
  let entryAnim: gsap.core.Timeline | null = null;
  let scrollEnergy = 0;
  let prevScroll = scroll;
  let lastCenter = -1;
  let hits: HitRect[] = [];
  const lastX: (number | undefined)[] = new Array(N);

  function layout() {
    hits = [];
    const half = W / 2;
    const buffer = PANEL_H;
    const cSrc = nearestIndex(scroll);
    const inEntry = entryState !== "done";
    // During the entry the panels are packed around the centred one at their growing size.
    const slotH = (s: number) => ENTRY.startH + (heightOf(s) - ENTRY.startH) * grow[s];

    panels.forEach((p, i) => {
      const src = sources[i];
      const centerX = centerOf(i) - scroll;
      if (!inEntry && (centerX < -half - buffer || centerX > half + buffer)) {
        p.mesh.visible = false;
        lastX[i] = undefined;
        return;
      }
      lastX[i] = centerX;
      if (src.tex && !p.bound) {
        p.mat.map = src.tex;
        p.mat.color.set(0xffffff);
        p.mat.needsUpdate = true;
        p.bound = true;
      }

      const shrink = 1 - 0.25 * scrollEnergy;
      let h = heightOf(i) * shrink;
      let w = src.aspect * h;
      let x = centerX;
      let y = 0;
      if (focus.active && focus.index === i) {
        w *= focusScale;
        h *= focusScale;
      } else if (drop[i] > 0) {
        y = -drop[i] * H * FOCUS.dropDist;
      }

      if (inEntry) {
        h = slotH(i);
        w = h * src.aspect;
        let off = 0;
        for (let k = cSrc; k < i; k++) off += (sources[k].aspect * slotH(k) + sources[k + 1].aspect * slotH(k + 1)) / 2 + GAP;
        for (let k = cSrc; k > i; k--) off -= (sources[k].aspect * slotH(k) + sources[k - 1].aspect * slotH(k - 1)) / 2 + GAP;
        x = off;
        if (x < -half - buffer || x > half + buffer) {
          p.mesh.visible = false;
          return;
        }
        const below = -H * ENTRY.fromBelow;
        y = below + (y - below) * pEntry[i];
      }

      p.mesh.visible = true;
      p.mesh.position.set(x, y, 0);
      p.mesh.scale.set(w, h, 1);
      const sx = x + W / 2;
      const sy = H / 2 - y;
      hits.push({ left: sx - w / 2, right: sx + w / 2, top: sy - h / 2, bottom: sy + h / 2, index: i });
    });
  }

  // ---- pointer: "View" cursor over a panel, click to focus or to go to an image
  const moveX = cursorElement ? gsap.quickTo(cursorElement, "x", { duration: 0.5, ease: "power3.out" }) : null;
  const moveY = cursorElement ? gsap.quickTo(cursorElement, "y", { duration: 0.5, ease: "power3.out" }) : null;
  if (cursorElement) gsap.set(cursorElement, { xPercent: 20, yPercent: 30, scale: 0, autoAlpha: 0 });
  let overPanel = false;
  let pointer: { x: number; y: number } | null = null;

  const hitAt = (x: number, y: number) => hits.find((r) => x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) ?? null;
  const local = (e: { clientX: number; clientY: number }) => {
    const r = el.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  function setView(on: boolean) {
    if (entryState !== "done" || focus.active) on = false;
    el.style.cursor = on ? "pointer" : "";
    if (on === overPanel || !cursorElement) return;
    overPanel = on;
    gsap.to(cursorElement, { scale: on ? 1 : 0, autoAlpha: on ? 1 : 0, duration: on ? 0.35 : 0.25, ease: on ? "power3.out" : "power3.in" });
  }
  function onPointerMove(e: PointerEvent) {
    if (e.pointerType !== "mouse") return;
    pointer = local(e);
    moveX?.(pointer.x);
    moveY?.(pointer.y);
    setView(hitAt(pointer.x, pointer.y) !== null);
  }
  function onPointerLeave() {
    pointer = null;
    setView(false);
  }
  function onClick(e: MouseEvent) {
    if (entryState !== "done" || focus.active) return;
    const p = local(e);
    const hit = hitAt(p.x, p.y);
    if (!hit) return;
    if (hit.index === nearestIndex(scroll) && Math.abs(target - scroll) < 4) openFocus();
    else options.onRequestIndex(hit.index);
  }
  el.addEventListener("pointermove", onPointerMove);
  el.addEventListener("pointerleave", onPointerLeave);
  el.addEventListener("click", onClick);

  // ---- focus: the centred image grows, the others fall away
  function openFocus() {
    const i = nearestIndex(scroll);
    if (focus.active || !sources[i].tex) return;
    focus.active = true;
    focus.index = i;
    focus.atProgress = progress;
    const others = panels
      .map((_, k) => k)
      .filter((k) => k !== i && lastX[k] !== undefined)
      .sort((a, b) => Math.abs(a - i) - Math.abs(b - i));
    focus.anim?.kill();
    const scaleProxy = { v: focusScale };
    const tl = gsap.timeline();
    tl.to(focus, { lensFx: 0, duration: FOCUS.lensFade, ease: "power3.out" }, 0);
    tl.to(scaleProxy, { v: FOCUS.centerScale, duration: FOCUS.focusDuration, ease: FOCUS.focusEase, onUpdate: () => void (focusScale = scaleProxy.v) }, 0);
    others.forEach((k) => tl.to(drop, { [k]: 1, duration: FOCUS.cardDuration, ease: FOCUS.cardEase }, (Math.abs(k - i) - 1) * FOCUS.stagger));
    focus.anim = tl;
    setView(false);
    options.onFocusChange(true);
  }

  function closeFocus() {
    if (!focus.active) return;
    focus.anim?.kill();
    options.onFocusChange(false);
    const i = focus.index;
    const scaleProxy = { v: focusScale };
    const tl = gsap.timeline({
      onComplete: () => {
        focus.active = false;
        focus.index = -1;
        if (pointer) setView(hitAt(pointer.x, pointer.y) !== null);
      },
    });
    tl.to(focus, { lensFx: 1, duration: FOCUS.lensFade * 0.8, ease: "power3.inOut" }, 0);
    tl.to(scaleProxy, { v: 1, duration: FOCUS.focusDuration * 0.85, ease: FOCUS.focusEase, onUpdate: () => void (focusScale = scaleProxy.v) }, 0);
    drop.forEach((d, k) => {
      if (d > 0) tl.to(drop, { [k]: 0, duration: FOCUS.cardDuration * 0.85, ease: FOCUS.cardEase }, Math.max(0, 3 - Math.abs(k - i)) * FOCUS.stagger * 0.7);
    });
    focus.anim = tl;
  }

  // ---- entry: panels rise from below, then grow from the centre outwards
  async function playEntry() {
    if (entryState !== "waiting") return;
    entryState = "rising";
    await Promise.race([ready, new Promise((r) => setTimeout(r, 2500))]);
    if (!running) return;
    options.onEntryDone(false);
    scroll = target;
    layout();
    const visible = panels.map((_, k) => k).filter((k) => lastX[k] !== undefined || Math.abs(k - nearestIndex(scroll)) < 6);
    const tl = gsap.timeline({ delay: 0.15 });
    const spread = ENTRY.stagger * Math.max(visible.length - 1, 1);
    let riseEnd = 0;
    visible.forEach((k) => {
      const at = Math.random() * spread;
      riseEnd = Math.max(riseEnd, at + ENTRY.riseDuration);
      tl.to(pEntry, { [k]: 1, duration: ENTRY.riseDuration, ease: ENTRY.riseEase }, at);
    });
    tl.call(() => void (entryState = "growing"), [], riseEnd);
    const c = nearestIndex(scroll);
    const maxRank = Math.max(...visible.map((k) => Math.abs(k - c)), 0);
    const growStart = riseEnd + ENTRY.growDelay;
    let growEnd = growStart;
    tl.to(focus, { lensFx: 1, duration: ENTRY.lensBloom, ease: ENTRY.lensBloomEase }, growStart);
    visible.forEach((k) => {
      const at = growStart + (maxRank - Math.abs(k - c)) * ENTRY.growStagger;
      growEnd = Math.max(growEnd, at + ENTRY.growDuration);
      tl.to(grow, { [k]: 1, duration: ENTRY.growDuration, ease: ENTRY.growEase }, at);
    });
    tl.call(
      () => {
        entryState = "done";
        grow.fill(1);
        pEntry.fill(1);
        options.onEntryDone(true);
      },
      [],
      growEnd,
    );
    entryAnim = tl;
  }

  // ---- render loop (stopped off screen)
  let raf = 0;
  let running = true;
  let visible = false;
  function tick() {
    if (!running) return;
    if (!visible || document.hidden) {
      raf = 0;
      return;
    }
    scroll += (target - scroll) * EASE;
    const ci = nearestIndex(scroll);
    if (ci !== lastCenter) {
      lastCenter = ci;
      options.onActiveChange(ci);
    }
    // panels shrink a little while the row moves fast
    const norm = Math.min(1, Math.abs(scroll - prevScroll) / SHRINK_MAX);
    prevScroll = scroll;
    scrollEnergy += (norm - scrollEnergy) * (norm > scrollEnergy ? SHRINK_ATTACK : SHRINK_DECAY);
    layout();

    lensUniforms.uAspect.value = W / H;
    lensUniforms.uTime.value = performance.now() * 0.001;
    for (const key of LENS_FX_KEYS) lensUniforms[key].value = lensFxFull[key] * focus.lensFx;

    renderer.setRenderTarget(rt);
    renderer.render(scene, camera);
    renderer.setRenderTarget(null);
    renderer.render(lensScene, lensCam);
    raf = requestAnimationFrame(tick);
  }
  const startLoop = () => {
    if (running && !raf) raf = requestAnimationFrame(tick);
  };
  startLoop();
  if (reduced) options.onEntryDone(true);

  function onResize() {
    W = Math.max(1, mount.clientWidth);
    H = Math.max(1, mount.clientHeight);
    PANEL_H = panelHFor();
    recompute();
    target = targetFor(progress);
    scroll = target;
    renderer.setSize(W, H);
    Object.assign(camera, { left: -W / 2, right: W / 2, top: H / 2, bottom: -H / 2 });
    camera.updateProjectionMatrix();
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    renderer.setPixelRatio(ratio);
    rt.setSize(W * ratio, H * ratio);
    lensUniforms.uRes.value.set(W * ratio, H * ratio);
  }
  const resizeObserver = new ResizeObserver(onResize);
  resizeObserver.observe(mount);
  const intersection = new IntersectionObserver(([entry]) => {
    visible = entry?.isIntersecting ?? true;
    if (visible) startLoop();
  });
  intersection.observe(mount);
  const onVisibility = () => !document.hidden && startLoop();
  document.addEventListener("visibilitychange", onVisibility);

  return {
    setProgress(p) {
      progress = Math.min(1, Math.max(0, p));
      target = targetFor(progress);
      // scrolling the page away from a focused image closes it
      if (focus.active && Math.abs(progress - focus.atProgress) > 0.01) closeFocus();
    },
    playEntry,
    closeFocus,
    destroy() {
      running = false;
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      intersection.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerleave", onPointerLeave);
      el.removeEventListener("click", onClick);
      focus.anim?.kill();
      entryAnim?.kill();
      if (cursorElement) gsap.killTweensOf(cursorElement);
      renderer.dispose();
      rt.dispose();
      lensQuad.geometry.dispose();
      lensMat.dispose();
      panels.forEach((p) => {
        p.mesh.geometry.dispose();
        p.mat.dispose();
      });
      sources.forEach((s) => s.tex?.dispose());
      el.remove();
    },
  };
}

const pad = (n: number) => String(n).padStart(2, "0");

/** Pinned image row with a liquid-glass lens, scrolled by the page. */
export function ScrollGlassCarousel({
  items,
  panelHeight = 520,
  gap = 16,
  background = "#ffffff",
  ringColor = "#009dff",
  scrollPerItem = 0.55,
  labels = { region: "Image carousel", view: "View", close: "Close", of: "of" },
  fallback = null,
  className = "",
}: ScrollGlassCarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const mountRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<Engine | null>(null);
  const [active, setActive] = useState(0);
  const [focused, setFocused] = useState(false);
  const [entryDone, setEntryDone] = useState(false);
  const [failed, setFailed] = useState(false);
  const N = items.length;
  // the engine only depends on the images; titles (e.g. a language switch) never rebuild it
  const itemsRef = useRef(items);
  const srcKey = items.map((i) => i.src).join("|");

  // page scroll position that centres image i
  const scrollTopFor = (i: number) => {
    const track = trackRef.current;
    if (!track || N < 2) return window.scrollY;
    const top = track.getBoundingClientRect().top + window.scrollY;
    return top + (i / (N - 1)) * (track.offsetHeight - window.innerHeight);
  };
  const goTo = (i: number) => {
    const clamped = Math.min(N - 1, Math.max(0, i));
    window.scrollTo({ top: scrollTopFor(clamped), behavior: prefersReducedMotion() ? "auto" : "smooth" });
  };
  const goToRef = useRef(goTo);
  // declared before the engine effect, so the engine sees the current items on mount
  useEffect(() => {
    itemsRef.current = items;
    goToRef.current = goTo;
  });

  useEffect(() => {
    const mount = mountRef.current;
    const track = trackRef.current;
    if (!mount || !track || N === 0) return;
    let engine: Engine | null = null;
    let frame = 0;
    let idle = 0;
    let entryIo: IntersectionObserver | null = null;

    // progress of the page scroll through the pinned track: 0 at the first image, 1 at the last
    const update = () => {
      frame = 0;
      const r = track.getBoundingClientRect();
      const total = r.height - window.innerHeight;
      engine?.setProgress(total > 0 ? -r.top / total : 0);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    const create = () => {
      engine = createCarousel(mount, cursorRef.current, {
        // local images go through the Next optimizer: WebP/AVIF at 1200 px instead of multi-MB originals
        items: itemsRef.current.map((it) => ({
          ...it,
          src: it.src.startsWith("/") ? `/_next/image?url=${encodeURIComponent(it.src)}&w=1200&q=75` : it.src,
        })),
        panelHeight,
        gap,
        background,
        ringColor,
        onActiveChange: setActive,
        onFocusChange: setFocused,
        onEntryDone: setEntryDone,
        onRequestIndex: (i) => goToRef.current(i),
      });
      if (!engine) {
        setFailed(true);
        return;
      }
      engineRef.current = engine;
      update();
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll);
      // the entry plays the first time the row reaches the screen
      entryIo = new IntersectionObserver(
        ([e]) => {
          if (e?.isIntersecting) {
            engine?.playEntry();
            entryIo?.disconnect();
          }
        },
        { threshold: 0.35 },
      );
      entryIo.observe(mount);
    };

    // Build the WebGL scene only when the section is ~1.5 screens away, while the browser is idle:
    // the page loads without this cost, and images and shaders are ready before the row shows up.
    const near = new IntersectionObserver(
      ([e]) => {
        if (!e?.isIntersecting) return;
        near.disconnect();
        idle = window.requestIdleCallback ? window.requestIdleCallback(create, { timeout: 600 }) : window.setTimeout(create, 50);
      },
      { rootMargin: "150% 0px" },
    );
    near.observe(track);

    return () => {
      near.disconnect();
      if (window.cancelIdleCallback) window.cancelIdleCallback(idle);
      window.clearTimeout(idle);
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      entryIo?.disconnect();
      engine?.destroy();
      engineRef.current = null;
    };
  }, [srcKey, N, panelHeight, gap, background, ringColor]);

  if (failed) return <>{fallback}</>;

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      goTo(active + (e.key === "ArrowRight" ? 1 : -1));
    } else if (e.key === "Escape") {
      engineRef.current?.closeFocus();
    }
  };

  const current = items[active] ?? items[0];
  const fade = (on: boolean) => ({ opacity: on ? 1 : 0, transition: "opacity .6s ease" });

  return (
    <div ref={trackRef} className={"relative " + className} style={{ height: `${100 + Math.max(0, N - 1) * scrollPerItem * 100}svh` }}>
      <div
        className="sticky top-0 h-svh w-full overflow-hidden outline-none focus-visible:ring-2 focus-visible:ring-current/30"
        style={{ background }}
        tabIndex={0}
        role="region"
        aria-roledescription="carousel"
        aria-label={labels.region}
        onKeyDown={onKeyDown}
      >
        <p className="sr-only" aria-live="polite">
          {current?.title}, {pad(active + 1)} {labels.of} {pad(N)}
        </p>

        <div ref={mountRef} className="absolute inset-0" />

        <p
          className="pointer-events-none absolute top-[9%] left-1/2 z-10 m-0 -translate-x-1/2 text-center font-(family-name:--pf-serif) text-2xl italic md:text-3xl"
          style={fade(entryDone)}
        >
          {current?.title}
        </p>
        <p className="pointer-events-none absolute bottom-[7%] left-1/2 z-10 m-0 -translate-x-1/2 text-sm tabular-nums" style={fade(entryDone && !focused)}>
          {pad(active + 1)}/{pad(N)}
        </p>

        <div ref={cursorRef} className="pointer-events-none absolute top-0 left-0 z-20 text-sm font-medium text-white mix-blend-difference">
          {labels.view}
        </div>

        <button
          type="button"
          onClick={() => engineRef.current?.closeFocus()}
          className="absolute top-[9%] right-[4%] z-20 text-sm font-medium transition-opacity duration-300"
          style={{ opacity: focused ? 1 : 0, pointerEvents: focused ? "auto" : "none" }}
        >
          {labels.close}
        </button>
      </div>
    </div>
  );
}

export default ScrollGlassCarousel;
